import identity from 'lodash/identity';

/**
 * Renames an oldKey to a newKey while preserving other properties and sort order of the object.
 * @param oldKey old key name
 * @param newKey new key name
 * @param object JS object
 * @returns object with renamed key
 */
export function renameKey<T>(
  oldKey: string,
  newKey: string,
  object: Record<string, T>,
) {
  return Object.keys(object).reduce<Record<string, T>>((acc, key) => {
    if (key === oldKey) {
      acc[newKey] = object[oldKey];
    } else {
      acc[key] = object[key];
    }
    return acc;
  }, {});
}

/**
 * Reduces an array into an object based on provided item key.
 */
export function fromArray<T extends object>(array: T[], key: string) {
  return array.reduce<Record<string, T>>((object, item) => {
    if (item.hasOwnProperty(key)) {
      object[String(item[key as keyof T])] = item;
    }
    return object;
  }, {});
}

export function tryDeserializeJsonObject(jsonString: string) {
  try {
    const output: unknown = JSON.parse(jsonString);
    if (typeof output === 'object' && output) {
      return output as Record<string, unknown>;
    }
  } catch {
    return null;
  }
  return null;
}

export function combineValueInObject<T>(
  obj1: Record<string, T[]>,
  obj2: Record<string, T[]>,
) {
  for (const key in obj1) {
    if (obj2[key]) {
      obj1[key] = [...new Set([...obj1[key], ...obj2[key]])];
    }
  }
  return obj1;
}

export function excludeValueInObject<T>(
  obj1: Record<string, T[]>,
  obj2: Record<string, T[]>,
) {
  for (const key in obj1) {
    if (obj2[key]) {
      obj1[key] = obj1[key].filter((v) => !obj2[key].includes(v));
    }
  }
  return obj1;
}

export function getObjectFlattenedValue<T>(obj: Record<string, T[]>) {
  return Object.values(obj).flat();
}

export const getValidEntity = <T>(
  entityMap: Record<string, T>,
  entityIds: Array<string | number>,
) => entityIds.map((id) => entityMap[id]).filter(identity);
